import {
  MotionmasterToolpath,
  MotionmasterToolpathBounds,
  MotionmasterToolpathMarker,
  MotionmasterToolpathSegment,
  MotionmasterXyz,
} from '../models/motionmaster-control.model';
import { readMotionmasterRingDraft, ringDraftMatchesPath } from './motionmaster-ring-draft.function';
import { verifiedKuhnHolePlane } from './motionmaster-kuhn-placement.function';
import { motionmasterSubroutineBody } from './motionmaster-job.function';

/**
 * Browser-side preview of the saved program text. This mirrors the Motion Master mini's
 * fagor_dnc/toolpath.py parser; it never contacts the machine and is not a collision check.
 * Parametric blocks are reported rather than guessed.
 */
type Axis = 'X' | 'Y' | 'Z' | 'U' | 'V' | 'A' | 'B' | 'C';
type LinearAxis = 'X' | 'Y' | 'Z';
type Position = Record<Axis, number>;
type Word = [string, number];

const LINEAR: LinearAxis[] = ['X', 'Y', 'Z'];
const AXES: Axis[] = ['X', 'Y', 'Z', 'U', 'V', 'A', 'B', 'C'];
const PLANES: Record<number, [LinearAxis, LinearAxis, LinearAxis]> = {
  17: ['X', 'Y', 'Z'],
  18: ['Z', 'X', 'Y'],
  19: ['Y', 'Z', 'X'],
};

function xyz(position: Position): MotionmasterXyz {
  return [position.X, position.Y, position.Z];
}

function extend(bounds: MotionmasterToolpathBounds | null, point: MotionmasterXyz): MotionmasterToolpathBounds {
  if (!bounds) return { min: [...point], max: [...point] };
  for (let i = 0; i < 3; i++) {
    bounds.min[i] = Math.min(bounds.min[i], point[i]);
    bounds.max[i] = Math.max(bounds.max[i], point[i]);
  }
  return bounds;
}

function distance(a: Position, b: Position): number {
  return Math.hypot(...LINEAR.map(axis => a[axis] - b[axis]));
}

function words(block: string): Word[] | null {
  const body = block.split(';', 1)[0].trim();
  if (!body) return [];
  if (/[XYZUVABCIJKRFS]\s*[-+]?P\d/i.test(body) || /[=*/]/.test(body)) return null;
  const matches: Word[] = [];
  const pattern = /([A-Z])\s*([-+]?(?:\d+\.?\d*|\.\d+))/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(body))) matches.push([match[1], Number(match[2])]);
  return matches;
}

function arcPoints(start: Position, end: Position, block: Word[], plane: number, clockwise: boolean): Position[] {
  const [ax, ay, az] = PLANES[plane];
  const sx = start[ax], sy = start[ay], ex = end[ax], ey = end[ay];
  const offsets = new Map(block.filter(([key]) => 'IJKR'.includes(key)));
  const centreKey: Record<LinearAxis, string> = { X: 'I', Y: 'J', Z: 'K' };
  let cx: number, cy: number;
  if (offsets.has('R')) {
    const radius = offsets.get('R') as number;
    const dx = ex - sx, dy = ey - sy, chord = Math.hypot(dx, dy);
    if (!chord || chord > 2 * Math.abs(radius) + 1e-9) return [{ ...start }, { ...end }];
    const height = Math.sqrt(Math.max(radius * radius - (chord / 2) ** 2, 0));
    const sign = clockwise !== (radius < 0) ? -1 : 1;
    cx = (sx + ex) / 2 + sign * height * (-dy / chord);
    cy = (sy + ey) / 2 + sign * height * (dx / chord);
  } else {
    cx = sx + (offsets.get(centreKey[ax]) ?? 0);
    cy = sy + (offsets.get(centreKey[ay]) ?? 0);
  }
  const radius = Math.hypot(sx - cx, sy - cy);
  const startAngle = Math.atan2(sy - cy, sx - cx);
  const endAngle = Math.atan2(ey - cy, ex - cx);
  let sweep = endAngle - startAngle;
  if (clockwise && sweep >= -1e-9) sweep -= 2 * Math.PI;
  if (!clockwise && sweep <= 1e-9) sweep += 2 * Math.PI;
  const steps = Math.max(2, Math.trunc(Math.abs(sweep) * 12) + 1);
  const points: Position[] = [];
  for (let i = 0; i <= steps; i++) {
    const fraction = i / steps, angle = startAngle + sweep * fraction;
    points.push({ ...start,
      [ax]: cx + radius * Math.cos(angle),
      [ay]: cy + radius * Math.sin(angle),
      [az]: start[az] + (end[az] - start[az]) * fraction,
    });
  }
  points[points.length - 1][ax] = ex;
  points[points.length - 1][ay] = ey;
  return points;
}

/** Fagor G9: absolute intermediate point I/J/K, non-modal, either sweep sense.
 * The third linear axis and rotary axes interpolate over the arc. */
function threePointArc(start: Position, end: Position, block: Word[], plane: number): Position[] {
  const [ax, ay, az] = PLANES[plane];
  const keys: Record<LinearAxis, string> = { X: 'I', Y: 'J', Z: 'K' };
  const midX = block.find(([k]) => k === keys[ax])?.[1];
  const midY = block.find(([k]) => k === keys[ay])?.[1];
  if (midX === undefined || midY === undefined) throw new Error('G9 intermediate point missing');
  const sx = start[ax], sy = start[ay], ex = end[ax], ey = end[ay];
  const bx = midX - sx, by = midY - sy, dx = ex - sx, dy = ey - sy;
  const determinant = 2 * (bx * dy - by * dx);
  if (Math.abs(determinant) < 1e-10) throw new Error('Degenerate G9 arc');
  const cx = sx + ((bx * bx + by * by) * dy - (dx * dx + dy * dy) * by) / determinant;
  const cy = sy + (bx * (dx * dx + dy * dy) - dx * (bx * bx + by * by)) / determinant;
  const norm = (a: number) => (a % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
  const a0 = Math.atan2(sy - cy, sx - cx);
  const through = norm(Math.atan2(midY - cy, midX - cx) - a0);
  const endSweep = norm(Math.atan2(ey - cy, ex - cx) - a0);
  const sweep = through <= endSweep + 1e-10 ? endSweep : endSweep - 2 * Math.PI;
  const radius = Math.hypot(sx - cx, sy - cy);
  const steps = Math.max(2, Math.ceil(Math.abs(sweep) / (Math.PI / 180)));
  return Array.from({ length: steps + 1 }, (_, i) => {
    const f = i / steps, a = a0 + sweep * f;
    return { ...start, [ax]: i === steps ? ex : cx + radius * Math.cos(a),
      [ay]: i === steps ? ey : cy + radius * Math.sin(a), [az]: start[az] + (end[az] - start[az]) * f };
  });
}

function tip(pivot: MotionmasterXyz, u: number, v: number, stickout: number): MotionmasterXyz {
  const ur = u * Math.PI / 180, vr = v * Math.PI / 180;
  const length = 6.3 + stickout;
  const round = (value: number): number => Math.round(value * 10000) / 10000;
  return [
    round(pivot[0] - length * Math.sin(ur) * Math.sin(vr)),
    round(pivot[1] + length * Math.cos(ur) * Math.sin(vr)),
    round(pivot[2] - length * Math.cos(vr)),
  ];
}

function addTips(segments: MotionmasterToolpathSegment[], stickout: number): MotionmasterToolpathBounds | null {
  let previousU = 0, previousV = 0;
  let bounds: MotionmasterToolpathBounds | null = null;
  for (const segment of segments) {
    // The first position has no known approach OR rotary start. Do not invent
    // a swing from U0/V0 at that position.
    if (segment.from_origin) {
      previousU = segment.u;
      previousV = segment.v;
    }
    const turning = Math.abs(segment.u - previousU) > 1e-9 || Math.abs(segment.v - previousV) > 1e-9;
    const steps = segment.points.length === 2 && turning
      ? Math.max(2, Math.trunc(Math.max(Math.abs(segment.u - previousU), Math.abs(segment.v - previousV)) / 5) + 1)
      : segment.points.length - 1;
    const samples: MotionmasterXyz[] = segment.points.length === 2 && turning
      ? Array.from({ length: steps + 1 }, (_, i) => segment.points[0].map((value, axis) =>
        value + (segment.points[1][axis] - value) * i / steps) as MotionmasterXyz)
      : segment.points;
    segment.tip = samples.map((point, i) => {
      const fraction = steps ? i / steps : 1;
      return tip(point,
        previousU + (segment.u - previousU) * fraction,
        previousV + (segment.v - previousV) * fraction,
        stickout);
    });
    if (segment.type !== 'rapid') for (const point of segment.tip) bounds = extend(bounds, point);
    previousU = segment.u;
    previousV = segment.v;
  }
  return bounds;
}

export function buildMotionmasterToolpath(text: string, options?: { subroutines: Record<number, string> }): MotionmasterToolpath {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let position: Position = { X: 0, Y: 0, Z: 0, U: 0, V: 0, A: 0, B: 0, C: 0 };
  let absolute = true, motion = 0, plane = 17, feed: number | null = null, started = false;
  let bounds: MotionmasterToolpathBounds | null = null;
  let cutBounds: MotionmasterToolpathBounds | null = null;
  let rapidLength = 0, feedLength = 0, minutes = 0;
  const segments: MotionmasterToolpathSegment[] = [];
  const markers: MotionmasterToolpathMarker[] = [];
  const unparsed: MotionmasterToolpath['unparsed'] = [];
  // Zero offsets as the control keeps them: a table per G54-G59, an active absolute one (G54-G57)
  // and an active additive one (G58/G59). Absolute X/Y/Z are programmed in the offset frame.
  const offsetTable: Record<number, Record<LinearAxis, number>> = {};
  const zeroOffset = (): Record<LinearAxis, number> => ({ X: 0, Y: 0, Z: 0 });
  let activeOffset = 0, activeAdditive = 0;
  const shift = (axis: LinearAxis): number =>
    (activeOffset ? offsetTable[activeOffset]?.[axis] ?? 0 : 0) + (activeAdditive ? offsetTable[activeAdditive]?.[axis] ?? 0 : 0);
  const shiftOf = (key: string): number => (LINEAR as string[]).includes(key) ? shift(key as LinearAxis) : 0;

  /**
   * One pass over a list of blocks. The main program runs at depth 0; a `(CALL n)` whose sub text
   * is known runs the sub's body at depth + 1 with the same state, every segment tagged with the
   * calling line so the program text highlights the call while the sub executes.
   */
  const run = (blocks: string[], lineOf: (index: number) => number, depth: number): void => {
    for (let index = 0; index < blocks.length; index++) {
      const line = lineOf(index), block = blocks[index].trim();
      if (depth === 0 && index === 0 && block.startsWith('%')) continue;
      if (!block || block.startsWith(';')) continue;
      if (/^\(\s*ORG[XYZUVABC]\d+\s*=/i.test(block)) {
        const assignment = /ORG([XYZ])(\d+)\s*=\s*([-+]?(?:\d+\.?\d*|\.\d+))/gi;
        let match: RegExpExecArray | null;
        while ((match = assignment.exec(block))) {
          const code = Number(match[2]);
          if (code < 54 || code > 59) continue;
          offsetTable[code] = offsetTable[code] ?? zeroOffset();
          offsetTable[code][match[1].toUpperCase() as LinearAxis] = Number(match[3]);
        }
        continue;
      }
      const call = /^\(\s*(PCALL|CALL|RPT|GOTO|SUB|RET|IF|MCALL|MDOFF)(?![A-Z])\s*([^)]*)\)/i.exec(block);
      if (call) {
        const kind = call[1].toUpperCase();
        if (kind === 'PCALL' || kind === 'CALL') {
          const diameter = /D\s*=?\s*([-+]?\d*\.?\d+)/i.exec(call[2]);
          const tool = /T\s*=?\s*([-+]?\d*\.?\d+)/i.exec(call[2]);
          const number = Number(/^\s*(\d+)/.exec(call[2])?.[1]);
          const holePlane = kind === 'PCALL' && options ? verifiedKuhnHolePlane(number, options.subroutines[number]) : null;
          const radius = diameter && tool ? (Number(diameter[1]) - Number(tool[1])) / 2 : 0;
          const canExpand = !!holePlane && Number.isFinite(radius) && radius > 0 && started &&
            /^\s*\d+,D[-+\d.]+,T[-+\d.]+\s*$/i.test(call[2]);
          const subBody = kind === 'CALL' && options && depth < 4 && Number.isFinite(number)
            ? motionmasterSubroutineBody(options.subroutines[number] ?? '', number) : null;
          markers.push({ line, kind: kind === 'PCALL' ? 'hole' : 'call',
            x: position.X, y: position.Y, z: position.Z, label: block,
            diameter: diameter ? Number(diameter[1]) : null, expanded: canExpand || !!subBody });
          if (canExpand && holePlane) {
            plane = holePlane;
            const [ax, ay] = PLANES[plane];
            const key: Record<LinearAxis, string> = { X: 'I', Y: 'J', Z: 'K' };
            let current = { ...position };
            const samples: Position[] = [];
            for (const [travel, offset] of [[radius, radius / 2], [0, -radius], [-radius, -radius / 2]]) {
              const next = { ...current, [ay]: current[ay] + travel };
              const points = arcPoints(current, next, [[key[ax], 0], [key[ay], offset]], plane, true);
              samples.push(...(samples.length ? points.slice(1) : points));
              current = next;
            }
            if (number === 100) feed = 50;
            const length = samples.slice(1).reduce((sum, p, i) => sum + distance(samples[i], p), 0);
            segments.push({ line, type: 'arc', points: samples.map(xyz), u: position.U, v: position.V, feed, length });
            for (const p of samples) { bounds = extend(bounds, xyz(p)); cutBounds = extend(cutBounds, xyz(p)); }
            feedLength += length;
            if (feed) minutes += length / feed;
            absolute = true;
            motion = 2;
          } else if (subBody) {
            run(subBody, () => line, depth + 1);
          } else if (options) unparsed.push({ line, text: block, why: 'unverified or unsupported subroutine' });
        } else if (kind === 'SUB') {
          // Subs appended after the main program end it; a program that is only a sub starts here.
          if (depth === 0 && started) return;
        } else if (kind === 'RET') {
          if (depth > 0 || started) return;
        } else unparsed.push({ line, text: block, why: kind.toLowerCase() });
        continue;
      }
      const parsed = words(block);
      if (parsed === null) {
        unparsed.push({ line, text: block, why: 'parametric' });
        continue;
      }
      if (!parsed.length) continue;
      let machineFrame = false;
      for (const [, value] of parsed.filter(([key]) => key === 'G')) {
        const code = Math.trunc(value);
        if ([0, 1, 2, 3].includes(code)) motion = code;
        else if (code === 90) absolute = true;
        else if (code === 91) absolute = false;
        else if (PLANES[code]) plane = code;
        else if (code === 53) machineFrame = true;
        else if (code >= 54 && code <= 57) activeOffset = code;
        else if (code === 58 || code === 59) activeAdditive = code;
        else if (code === 4) {
          const k = parsed.find(([key]) => key === 'K')?.[1];
          markers.push({ line, kind: 'dwell', x: position.X, y: position.Y, z: position.Z,
            label: k === undefined ? 'dwell' : `dwell ${k}` });
        }
      }
      for (const [, value] of parsed.filter(([key]) => key === 'M')) {
        const code = Math.trunc(value);
        const label = ({ 0: 'program stop', 30: 'end', 3: 'spindle CW', 4: 'spindle CCW', 5: 'spindle stop' } as Record<number, string>)[code];
        if (!label) continue;
        const spindle = parsed.find(([key]) => key === 'S')?.[1];
        markers.push({ line, kind: 'm', x: position.X, y: position.Y, z: position.Z,
          label: spindle !== undefined && (code === 3 || code === 4) ? `${label} S${spindle}` : label });
      }
      const newFeed = parsed.find(([key]) => key === 'F')?.[1];
      if (newFeed !== undefined) feed = newFeed;
      const target = { ...position };
      const isG9 = parsed.some(([key, value]) => key === 'G' && value === 9);
      let moved = false;
      for (const [key, value] of parsed) {
        if (!AXES.includes(key as Axis)) continue;
        const axis = key as Axis;
        target[axis] = absolute ? value + (machineFrame ? 0 : shiftOf(axis)) : position[axis] + value;
        moved = true;
      }
      if (!moved) {
        // G53 alone cancels the active zero offset.
        if (machineFrame) { activeOffset = 0; activeAdditive = 0; }
        continue;
      }
      // G9 I/J/K are an absolute intermediate point, so they live in the offset frame too.
      const centreKey: Record<string, LinearAxis> = { I: 'X', J: 'Y', K: 'Z' };
      const frame: Word[] = isG9 && absolute && !machineFrame
        ? parsed.map(([key, value]) => (centreKey[key] ? [key, value + shift(centreKey[key])] : [key, value]) as Word)
        : parsed;
      if (!started) {
        started = true;
        segments.push({ line, type: 'rapid', points: [xyz(target), xyz(target)],
          u: target.U, v: target.V, feed: null, length: 0, from_origin: true });
        bounds = extend(bounds, xyz(target));
        position = target;
        continue;
      }
      if (isG9 || motion === 2 || motion === 3) {
        let arc: Position[];
        try {
          if (isG9 && !absolute) throw new Error('Incremental G9 preview unsupported');
          arc = isG9 ? threePointArc(position, target, frame, plane) : arcPoints(position, target, frame, plane, motion === 2);
        } catch (error) {
          unparsed.push({ line, text: block, why: error instanceof Error ? error.message : 'invalid arc' });
          position = target;
          continue;
        }
        const length = arc.slice(1).reduce((sum, point, i) => sum + distance(arc[i], point), 0);
        segments.push({ line, type: 'arc', points: arc.map(xyz), u: target.U, v: target.V, feed, length });
        feedLength += length;
        for (const point of arc) {
          bounds = extend(bounds, xyz(point));
          cutBounds = extend(cutBounds, xyz(point));
        }
        if (feed) minutes += length / feed;
      } else {
        const length = distance(position, target);
        const rapid = motion === 0;
        segments.push({ line, type: rapid ? 'rapid' : 'feed', points: [xyz(position), xyz(target)],
          u: target.U, v: target.V, feed: rapid ? null : feed, length });
        if (rapid) {
          rapidLength += length;
          minutes += length / 1000;
        } else {
          feedLength += length;
          cutBounds = extend(cutBounds, xyz(position));
          cutBounds = extend(cutBounds, xyz(target));
          if (feed) minutes += length / feed;
        }
        bounds = extend(bounds, xyz(target));
      }
      position = target;
    }
  };
  run(lines, index => index + 1, 0);

  const candidate = readMotionmasterRingDraft(text);
  const stickout = candidate?.stickout ?? Number(/(\d+\.?\d*)\s*"?\s*FROM COLLET FACE/i.exec(text)?.[1] ?? 3);
  const tipBounds = addTips(segments, stickout);
  const ringDraft = candidate && !unparsed.length && ringDraftMatchesPath(candidate, segments) ? candidate : undefined;
  if (text.includes(';@RING-DRAFT') && !ringDraft) {
    unparsed.push({ line: lines.findIndex(line => line.startsWith(';@RING-DRAFT')) + 1,
      text: ';@RING-DRAFT', why: 'ring context does not match the saved motion; stock preview hidden' });
  }
  return {
    segments, markers, unparsed, bounds, cut_bounds: cutBounds ?? bounds, tip_bounds: tipBounds,
    stickout, ringDraft,
    stats: { blocks: segments.length, rapid_length: rapidLength, feed_length: feedLength,
      est_minutes: minutes, holes: markers.filter(marker => marker.kind === 'hole').length,
      end: xyz(position) },
  };
}
